import { useRef } from 'react';
import { Upload } from 'lucide-react';
import { parsePortableTimeline, portableToCreateInput } from '../../features/timelinePortable';
import { useCreateTimeline } from '../../hooks/useEventOps';
import { useDialog } from '../../context/DialogContext';

interface ImportTimelineButtonProps {
  eventId?: string;
  isTemplate?: boolean;
  onSuccess?: () => void;
  className?: string;
}

export function ImportTimelineButton({
  eventId,
  isTemplate,
  onSuccess,
  className,
}: ImportTimelineButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const createTimeline = useCreateTimeline();
  // Captured at component top level — the FileReader callback isn't a hook context.
  const { alert: showAlert } = useDialog();

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const portable = parsePortableTimeline(e.target?.result as string);
        const input = portableToCreateInput(portable, { eventId, isTemplate });
        createTimeline.mutate(input, { onSuccess });
      } catch (err) {
        void showAlert({
          title: 'Import Failed',
          message: err instanceof Error ? err.message : 'Import failed.',
          variant: 'error',
        });
      }
    };
    reader.readAsText(file);
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".json,application/json"
        onChange={handleChange}
        className="hidden"
        data-testid="import-timeline-input"
      />
      <button
        onClick={() => inputRef.current?.click()}
        disabled={createTimeline.isPending}
        className={
          className ??
          'flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50'
        }
        data-testid="import-timeline-button"
      >
        <Upload size={14} />
        {createTimeline.isPending ? 'Importing…' : 'Import'}
      </button>
    </>
  );
}
